import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ChevronRight, Loader2, MapPin, Plus, X } from 'lucide-react'
import {
  createSite,
  deleteSite,
  fetchOrganizations,
  updateSite,
} from '@/lib/api/spaces.js'
import {
  initialModelForm,
  persistEntityModel,
  pickModelAsset,
} from '@/lib/modelPresets.js'
import ModelFormSection from '@/components/common/ModelFormSection.jsx'
import RowMenu from '@/components/common/RowMenu.jsx'
import OrgMark from '@/components/common/OrgMark.jsx'
import TimezoneSelect from '@/components/common/TimezoneSelect.jsx'

export default function SpacesPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: orgs = [], isLoading } = useQuery({
    queryKey: ['orgs'],
    queryFn: fetchOrganizations,
  })
  const [dialog, setDialog] = useState(null)

  const invalidateOrgs = () => queryClient.invalidateQueries({ queryKey: ['orgs'] })

  const createSiteMutation = useMutation({
    mutationFn: async ({ orgId, values }) => {
      const { siteModel, ...siteFields } = values
      const site = await createSite(orgId, siteFields)
      await persistEntityModel('site', site.id, null, siteModel)
      return site
    },
    onSuccess: () => { invalidateOrgs(); setDialog(null) },
  })
  const updateSiteMutation = useMutation({
    mutationFn: async ({ siteId, site, values }) => {
      const { siteModel, ...siteFields } = values
      const updated = await updateSite(siteId, siteFields)
      await persistEntityModel('site', siteId, site, siteModel)
      return updated
    },
    onSuccess: () => { invalidateOrgs(); setDialog(null) },
  })
  const deleteSiteMutation = useMutation({
    mutationFn: deleteSite,
    onSuccess: invalidateOrgs,
  })

  const sites = useMemo(() => {
    const list = []
    for (const org of orgs) {
      for (const site of org.sites || []) {
        list.push({ site, org })
      }
    }
    return list
  }, [orgs])

  const handleDeleteSite = (site) => {
    if (window.confirm(`Delete "${site.name}"? This also removes all buildings and floors inside it.`)) {
      deleteSiteMutation.mutate(site.id)
    }
  }

  const handleDialogSubmit = (values) => {
    if (dialog.mode === 'create') {
      createSiteMutation.mutate({ orgId: values.orgId, values: stripOrgId(values) })
    } else {
      updateSiteMutation.mutate({
        siteId: dialog.site.id,
        site: dialog.site,
        values: stripOrgId(values),
      })
    }
  }

  const submitting = createSiteMutation.isPending || updateSiteMutation.isPending
  const error =
    createSiteMutation.error?.uiMessage ||
    updateSiteMutation.error?.uiMessage ||
    null
  const deleteError = deleteSiteMutation.error?.uiMessage

  const canCreate = orgs.length > 0

  return (
    <main className="app-content">
      <header className="page-header">
        <div>
          <h1 className="page-header__title">Spaces</h1>
          <div className="page-header__sub">
            Sites across every organization. Open a site to manage its buildings and floors.
          </div>
        </div>
        {canCreate && (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setDialog({ mode: 'create' })}
          >
            <Plus size={14} /> New site
          </button>
        )}
      </header>

      {deleteError && (
        <div className="auth-error" role="alert" style={{ marginBottom: 12 }}>
          <span>{deleteError}</span>
          <button type="button" className="btn btn--sm" onClick={() => deleteSiteMutation.reset()}>
            Dismiss
          </button>
        </div>
      )}

      <section className="card card--lg card--flush">
        {isLoading ? (
          <div className="table__empty">Loading sites...</div>
        ) : !canCreate ? (
          <div className="table__empty">
            <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
              No organizations yet
            </div>
            <div>Add an organization from the Organizations tab to start mapping spaces.</div>
          </div>
        ) : sites.length === 0 ? (
          <div className="table__empty">
            <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
              No sites yet
            </div>
            <div style={{ marginBottom: 14 }}>Create one to begin mapping buildings and floors.</div>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => setDialog({ mode: 'create' })}
            >
              <Plus size={14} /> New site
            </button>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Site</th>
                <th>Organization</th>
                <th>Address</th>
                <th>Buildings</th>
                <th>Floors</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {sites.map(({ site, org }) => {
                const buildingCount = (site.buildings || []).length
                const floorCount = countFloors(site)
                const open = () => navigate(`/spaces/sites/${site.id}`)
                const onKeyDown = (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    open()
                  }
                }
                return (
                  <tr
                    key={site.id}
                    className="is-interactive"
                    role="link"
                    tabIndex={0}
                    onClick={open}
                    onKeyDown={onKeyDown}
                  >
                    <td>
                      <div className="row">
                        <OrgMark />
                        <div>
                          <div style={{ fontWeight: 600 }}>{site.name}</div>
                          <div className="muted" style={{ fontSize: 12 }}>{site.slug || site.timezone || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{org.name}</div>
                      <div className="muted" style={{ fontSize: 12 }}>{org.slug}</div>
                    </td>
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        <MapPin size={14} color="var(--text-muted)" />
                        <span className="muted" style={{ fontSize: 13 }}>
                          {site.address || '—'}
                        </span>
                      </div>
                    </td>
                    <td>{buildingCount}</td>
                    <td>{floorCount}</td>
                    <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
                        <ChevronRight size={16} color="var(--text-muted)" />
                        <RowMenu
                          label="Site actions"
                          onEdit={() => setDialog({ mode: 'edit', site, org })}
                          onDelete={() => handleDeleteSite(site)}
                        />
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </section>

      {dialog && (
        <SiteDialog
          dialog={dialog}
          orgs={orgs}
          submitting={submitting}
          error={error}
          onClose={() => {
            createSiteMutation.reset()
            updateSiteMutation.reset()
            setDialog(null)
          }}
          onSubmit={handleDialogSubmit}
        />
      )}
    </main>
  )
}

function SiteDialog({ dialog, orgs, submitting, error, onClose, onSubmit }) {
  const isEdit = dialog.mode === 'edit'
  const [values, setValues] = useState(() => ({
    name: dialog.site?.name || '',
    address: dialog.site?.address || '',
    timezone: dialog.site?.timezone || '',
    orgId: dialog.org?.id || orgs[0]?.id || '',
  }))
  const [siteModel, setSiteModel] = useState(() =>
    initialModelForm(pickModelAsset(dialog.site)),
  )

  useEffect(() => {
    setValues({
      name: dialog.site?.name || '',
      address: dialog.site?.address || '',
      timezone: dialog.site?.timezone || '',
      orgId: dialog.org?.id || orgs[0]?.id || '',
    })
    setSiteModel(initialModelForm(pickModelAsset(dialog.site)))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dialog.site is a new object each orgs fetch; ids keep the form stable while the modal is open
  }, [dialog.mode, dialog.site?.id, dialog.org?.id, orgs])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const update = (field, value) => setValues((current) => ({ ...current, [field]: value }))

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!values.name.trim() || submitting) return
    if (!isEdit && !values.orgId) return
    onSubmit({
      orgId: values.orgId,
      name: values.name.trim(),
      address: values.address.trim(),
      timezone: values.timezone.trim(),
      siteModel,
    })
  }

  const patchSiteModel = (patch) => {
    setSiteModel((cur) => (typeof patch === 'function' ? patch(cur) : patch))
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="site-dialog-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal__header">
          <h2 id="site-dialog-title" className="modal__title">
            {isEdit ? 'Edit site' : 'New site'}
          </h2>
          <button type="button" className="icon-btn icon-btn--sm" aria-label="Close" onClick={onClose}>
            <X />
          </button>
        </div>

        <form className="modal__form" onSubmit={handleSubmit}>
          {!isEdit && (
            <label className="auth-field">
              <span className="auth-field__label">Organization</span>
              <select
                required
                value={values.orgId}
                onChange={(e) => update('orgId', e.target.value)}
              >
                {orgs.map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </label>
          )}

          <label className="auth-field">
            <span className="auth-field__label">Name</span>
            <input
              autoFocus
              required
              value={values.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="Sacramento VA Medical Center"
            />
          </label>

          <label className="auth-field">
            <span className="auth-field__label">Address</span>
            <input
              value={values.address}
              onChange={(e) => update('address', e.target.value)}
              placeholder="10535 Hospital Way, Mather, CA"
            />
          </label>

          <label className="auth-field">
            <span className="auth-field__label">Timezone</span>
            <TimezoneSelect
              value={values.timezone}
              onChange={(v) => update('timezone', v)}
            />
          </label>

          <ModelFormSection
            scope="site"
            entityId={dialog.site?.id || null}
            form={siteModel}
            onChange={patchSiteModel}
          />

          {error && (
            <div className="auth-error" role="alert">
              <span>{error}</span>
            </div>
          )}

          <div className="modal__actions">
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={submitting || !values.name.trim()}>
              {submitting ? (
                <><Loader2 size={14} className="spin" /> Saving...</>
              ) : isEdit ? 'Save changes' : 'Create site'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function stripOrgId(values) {
  const { orgId: _orgId, ...rest } = values
  return rest
}

function countFloors(site) {
  return (site.buildings || []).reduce(
    (sum, building) => sum + (building.floors || []).length,
    0,
  )
}
